import { fillPath, route } from "./routes.js";
import { adminUserViewSchema } from "./users.js";
import { z } from "zod";
import { RESERVED_ATTRIBUTE_KEY_MESSAGE, RESOURCE_SLUG_PATTERN, isReservedAttributeKey } from "@zion/sdk-contracts";
//#region src/groups.ts
/**
* `/v1/admin/groups/*` — the eleven routes the admin app's groups pages and sdk-node's `GroupsClient` call.
* 2026-10-02-directory-asks-platform added two (#3597): the remove-edge route and the move route.
*
* Producer: `AdminGroupsController` and `GroupService` in `cloud/dp/core-api/src/admin/`.
* Consumer: the admin app's `AdminClient`. The shell's read types are narrower than the bodies in
* four places:
*
* 1. `GroupRow` has nine columns. The shell's `GroupRecord` declares five and omits
*    `organizationId`, `description`, `externalId` and `updatedAt`. Every group route returns the
*    full row.
* 2. `GroupService.getGroup` returns `members: UserPublicRow[]` verbatim — 13 columns per member.
*    The shell's `GroupMember` declares three.
* 3. `roles` on the detail body is always computed by `getGroup`. The shell marks it optional for a
*    rolling-deploy reason; it is required here.
* 4. `deleteGroup`, `addGroupMember` and `removeGroupMember` are typed `Promise<void>` in the shell
*    client. All three controllers return a one-key `{ message }` body.
*
* Permission keys: `groups:read` on the two reads, `groups:manage` on create, update, delete, the
* two membership writes and the three child-group writes, and `channels:manage` on `PUT {id}/channel` — the
* permission follows the resource being changed, and what changes there is release-channel routing.
* From 2026-09-30-agent-streams-and-app-owned-groups (decision 9, R-012), update, delete, the two
* membership writes and the child-group write also admit a holder of `group:manage` on the group,
* and create also admits an app bearer whose org consented to `platform:groups:manage`. The detail
* read admits `group:manage` on the group, and the list read with `managedByMe=true` admits any
* caller of the organization. The org key keeps every write.
*
* Members (decision 7). A member is any principal of the organization: a person or a machine
* principal. A person is a `users` row, or a `human`-type `machine_principals` row that no `users`
* row matches. Every member carries `principalId`, the platform row id (the index's identifier
* rule), `kind` and `displayName`; {@link groupMemberSchema} reads the three shapes.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*
* One exception: {@link groupMemberSchema} refuses a member whose `kind` this version does not
* list, and that refusal fails the whole detail body. A member's `kind` decides which fields it
* carries, so a client that cannot place the kind cannot read the member (R-002).
*/
/** The most entries one group's attribute map holds. */
const GROUP_ATTRIBUTES_MAX_ENTRIES = 32;
/** The longest attribute key, in characters. */
const GROUP_ATTRIBUTE_KEY_MAX_LENGTH = 64;
/** The longest attribute value, in characters. */
const GROUP_ATTRIBUTE_VALUE_MAX_LENGTH = 1024;
/** The largest attribute map, in UTF-8 bytes of its `JSON.stringify` form: 16 KiB. */
const GROUP_ATTRIBUTES_MAX_BYTES = 16384;
/**
* The attribute key grammar: an app slug, one dot, then the app's own key. The slug part is the
* app slug grammar, `RESOURCE_SLUG_PATTERN` from `@zion/sdk-contracts` without its anchors: two or
* more lower-case letters, digits and hyphens, with a letter or digit at each end. The key part is
* upper-case and lower-case letters, digits, `_`, `.` and `-`. Examples: `directory.teamKind`,
* `notes.color`. A key with no dot, an upper-case slug, a space, a slug with a hyphen at an end
* (`-.x`) or a one-character slug (`0.0`) is refused (D-03). The slug part is a namespace by
* convention; the platform does not check it against the caller's app.
*/
const GROUP_ATTRIBUTE_KEY_PATTERN = new RegExp(`^${RESOURCE_SLUG_PATTERN.source.slice(1, -1)}\\.[A-Za-z0-9_.-]+$`);
/** The 400 message for a map with more than {@link GROUP_ATTRIBUTES_MAX_ENTRIES} entries. */
const GROUP_ATTRIBUTES_TOO_MANY_MESSAGE = "attributes holds at most 32 entries";
/** The 400 message for a map over {@link GROUP_ATTRIBUTES_MAX_BYTES} serialised bytes. */
const GROUP_ATTRIBUTES_TOO_LARGE_MESSAGE = "attributes serialises to at most 16384 bytes";
/**
* One attribute key: at most 64 characters in the {@link GROUP_ATTRIBUTE_KEY_PATTERN} grammar, and
* none of `__proto__`, `constructor` and `prototype` (`RESERVED_ATTRIBUTE_KEYS` in
* `@zion/sdk-contracts`). The grammar refuses those three keys too; the reserved-key check names
* the reason with `RESERVED_ATTRIBUTE_KEY_MESSAGE`, and it stays if the grammar widens.
*/
const groupAttributeKeySchema = z.string().refine((key) => !isReservedAttributeKey(key), { message: RESERVED_ATTRIBUTE_KEY_MESSAGE }).max(64).regex(GROUP_ATTRIBUTE_KEY_PATTERN);
/** One attribute value: a string of at most 1024 characters. The empty string is a value. */
const groupAttributeValueSchema = z.string().max(GROUP_ATTRIBUTE_VALUE_MAX_LENGTH);
/** The UTF-8 byte length of a map's `JSON.stringify` form. */
function serialisedBytes(map) {
	return new TextEncoder().encode(JSON.stringify(map)).length;
}
/**
* A group's whole attribute map as a writer may store it, under the I7 caps:
*
* 1. every key matches {@link GROUP_ATTRIBUTE_KEY_PATTERN} and has at most 64 characters;
* 2. every value is a string of at most 1024 characters;
* 3. at most {@link GROUP_ATTRIBUTES_MAX_ENTRIES} entries;
* 4. at most {@link GROUP_ATTRIBUTES_MAX_BYTES} bytes as UTF-8 JSON.
*
* Callers: {@link createGroupRequestSchema} for the create body, and core-api's `updateGroup`
* (WS-1), which parses the result of {@link applyGroupAttributesPatch} with this schema and answers
* 400 when the merged map breaks a cap. sdk-node's `FakeGroupsClient` (WS-6) applies the same
* rule. `{}` passes.
*/
const groupAttributesSchema = z.record(groupAttributeKeySchema, groupAttributeValueSchema).refine((map) => Object.keys(map).length <= 32, { message: GROUP_ATTRIBUTES_TOO_MANY_MESSAGE }).refine((map) => serialisedBytes(map) <= GROUP_ATTRIBUTES_MAX_BYTES, { message: GROUP_ATTRIBUTES_TOO_LARGE_MESSAGE });
/**
* The attribute patch of `PATCH /v1/admin/groups/{id}`: a key with a string sets that key, a key
* with `null` deletes it, and a key the patch does not name keeps its stored value. Each key and
* each string value obey the {@link groupAttributesSchema} rules, and the patch itself obeys the
* entry cap and the byte cap. A `null` for a key the group does not hold changes nothing.
*
* The merged map can still break a cap (33 stored keys after a patch of 2 new ones), so core-api
* checks the merge too; see {@link applyGroupAttributesPatch}.
*/
const groupAttributesPatchSchema = z.record(groupAttributeKeySchema, groupAttributeValueSchema.nullable()).refine((map) => Object.keys(map).length <= 32, { message: GROUP_ATTRIBUTES_TOO_MANY_MESSAGE }).refine((map) => serialisedBytes(map) <= GROUP_ATTRIBUTES_MAX_BYTES, { message: GROUP_ATTRIBUTES_TOO_LARGE_MESSAGE });
/**
* The map a group holds after a patch: a copy of `current`, then every string of `patch` set and
* every `null` key of `patch` deleted. Neither argument changes. The result's keys are in the
* order of `current`, then the new keys of `patch` in its own order.
*
* A patch key in `RESERVED_ATTRIBUTE_KEYS` (`__proto__`, `constructor`, `prototype`) changes
* nothing: the merge skips it. The schemas refuse such a key first; the skip is the second guard.
* The merge runs over a `Map` and returns `Object.fromEntries`, so no request key is an assignment
* target on a plain object, and the result's prototype is `Object.prototype`.
*
* The result is unchecked: parse it with {@link groupAttributesSchema} to apply the caps. Callers:
* core-api's `GroupService.updateGroup` (WS-1) and sdk-node's `FakeGroupsClient.update` (WS-6), so
* the server and the fake merge with one rule. Pure; never throws.
*/
function applyGroupAttributesPatch(current, patch) {
	const merged = new Map(Object.entries(current));
	for (const [key, value] of Object.entries(patch)) {
		if (isReservedAttributeKey(key)) continue;
		if (value === null) merged.delete(key);
		else merged.set(key, value);
	}
	return Object.fromEntries(merged);
}
/**
* A `auth.groups` row, which is what every route in this file returns at the top level. Ten
* fields: the nine columns before migration 0055 and `attributes`.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/groups.ts`. `externalProvider` and `externalId` are
* set by SCIM and null for a group an admin created.
*/
const groupRecordSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	name: z.string(),
	slug: z.string(),
	description: z.string().nullable(),
	externalProvider: z.string().nullable(),
	externalId: z.string().nullable(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	/**
	* The group's attribute map (#3598). A response schema reads it without the caps. A body with
	* no `attributes` key parses as `{}`: a core-api from before migration 0055 stores no
	* attributes, so `{}` is the true answer for it, and a client on this version can read that
	* data plane.
	*/
	attributes: z.record(z.string(), z.string()).default(() => ({}))
});
/**
* One row of `GET /v1/admin/groups`: the group row plus the count of principal members.
*
* `memberCount` is on the list route only — `GroupService.listGroups` computes it and neither
* `createGroup` nor `getGroup` does.
*
* Owner: `GroupWithCount` in `cloud/dp/core-api/src/admin/group.service.ts`.
*/
const groupListItemSchema = groupRecordSchema.extend({ memberCount: z.number().int().min(0) });
/**
* The member kinds of a group, in a fixed order. `human` is a person: a `users` row, or a
* `human`-type `machine_principals` row with no `users` row. `agent`, `worker`, `service` and
* `external` are `machine_principals` rows of that `principal_type`, the machine kinds of
* `MachinePrincipalType` in `@zion/sdk-contracts` (decision 7: any person or agent of the
* organization). `external` is a system outside the platform that logs in through a trust policy
* or a registered key (2026-10-01-dp-oidc-federation, R-012); it is appended last, so every
* earlier kind keeps its position. Frozen.
*/
const GROUP_MEMBER_KINDS = Object.freeze([
	"human",
	"agent",
	"worker",
	"service",
	"external"
]);
/** The machine member kinds: every {@link GROUP_MEMBER_KINDS} member but `human`. Frozen. */
const MACHINE_GROUP_MEMBER_KINDS = Object.freeze([
	"agent",
	"worker",
	"service",
	"external"
]);
/**
* A person inside `GET /v1/admin/groups/{id}`.
*
* The 13-column `userPublicColumns` projection `GroupService.getGroup` reads through
* `ctx.users.findById`, plus `principalId` and `kind: 'human'`. It is derived from
* `adminUserViewSchema` in `users.ts` rather than respelled, because it is the same projection
* minus the three fields `toAdminUserView` adds, and `toAdminUserView` does not run on this path.
*
* `principalId` equals `id` (the `users.id`); a body where the two differ is refused, so a client
* can key a member by `principalId` whatever its kind. The shell's `GroupMember` is
* `{ id, email, displayName }`.
*
* Owner: `userPublicColumns` in `cloud/dp/core-api/src/db/schema/auth/users.ts`, and WS-4's
* `GroupService.getGroup`, which adds the two keys.
*/
const humanGroupMemberSchema = adminUserViewSchema.omit({
	roles: true,
	isActive: true,
	fullName: true
}).extend({
	principalId: z.uuid(),
	kind: z.literal("human")
}).refine((member) => member.principalId === member.id, {
	message: "principalId must equal id for a human member",
	path: ["principalId"]
});
/**
* A machine principal inside `GET /v1/admin/groups/{id}`: an agent, a worker, a service or an
* `external` machine (R-012).
*
* - `principalId`: the `machine_principals.id`, the JWT `sub` of that principal's bearer. Any RFC
*   UUID version, because an operator-derived service id is a v8 UUID (`servicePrincipalId`).
* - `kind`: the row's `principal_type`, one of {@link MACHINE_GROUP_MEMBER_KINDS}.
* - `displayName`: the row's `display_name`.
*
* A machine row has no email, role or login, so none of the person columns appear here.
*
* Owner: WS-4's `GroupService.getGroup` (2026-09-30-agent-streams-and-app-owned-groups).
*/
const machineGroupMemberSchema = z.object({
	principalId: z.uuid(),
	kind: z.enum(MACHINE_GROUP_MEMBER_KINDS),
	displayName: z.string()
});
/**
* A person inside `GET /v1/admin/groups/{id}` whom no `users` row matches: a `human`-type
* `machine_principals` row whose id is not a `users.id`.
*
* core-api mints such a row when a JWT caller enrols a `human` principal with no `principalId`:
* `MachineIdentityService.enroll` takes the column's random default id. Decision 7 admits any person
* of the organization, so the row can be a member, and it carries the machine shape:
*
* - `principalId`: the `machine_principals.id`. Any RFC UUID version.
* - `kind`: `'human'`.
* - `displayName`: the row's `display_name`.
* - `id` and `email`: never present. A body with either is a {@link humanGroupMemberSchema}
*   person, and a person body that fails that schema is refused; it is never read as this shape
*   with its person columns stripped.
*
* A caller that holds `group:manage` on the group and no `groups:read` org key may read a person
* who has a `users` row in this shape too, with no email or login columns (R-002). A client keys
* every person by `principalId` and never assumes the 13 columns.
*
* Owner: WS-4's `GroupService.getGroup` (2026-09-30-agent-streams-and-app-owned-groups).
*/
const humanPrincipalGroupMemberSchema = z.object({
	principalId: z.uuid(),
	kind: z.literal("human"),
	displayName: z.string(),
	id: z.never().optional(),
	email: z.never().optional()
});
/**
* One member inside `GET /v1/admin/groups/{id}`, in one of three shapes:
*
* 1. {@link humanGroupMemberSchema}: a person with a `users` row, `kind: 'human'`.
* 2. {@link humanPrincipalGroupMemberSchema}: a person with no `users` row, `kind: 'human'`.
* 3. {@link machineGroupMemberSchema}: an agent, a worker, a service or an `external` machine.
*
* The schema tries the three in that order and returns the first that parses. A body with no
* `kind`, a `kind` outside {@link GROUP_MEMBER_KINDS}, or a person body that fails shape 1 is
* refused. Two people share the `human` kind, so the union is a plain `z.union`: zod 4's
* `z.discriminatedUnion` refuses a repeated discriminator value.
*
* Owner: WS-4's `GroupService.getGroup`. Consumers: the admin app's `AdminClient` and WS-5's
* `GroupsClient`, which reads `principalId`, `kind` and `displayName` from every shape.
*/
const groupMemberSchema = z.union([
	humanGroupMemberSchema,
	humanPrincipalGroupMemberSchema,
	machineGroupMemberSchema
]);
/**
* A channel assignment as it appears nested inside `GET /v1/admin/groups/{id}`, where
* `getGroup` joins the channel to get its name. `channelName` is `'Unknown'` when the channel row
* no longer resolves.
*
* Prefixed `Admin` because `ChannelAssignment` in `@zion/sdk-contracts` is the full
* `release.channel_assignments` row and shares nothing with this two-field projection.
*
* Owner: `GroupService.getGroup`.
*/
const adminChannelAssignmentSchema = z.object({
	channelId: z.uuid(),
	channelName: z.string()
});
/**
* A direct child group inside `GET /v1/admin/groups/{id}`: the `id`, `slug` and `name` of a group
* nested under this one through `POST /v1/admin/groups/{id}/child-groups` (R-003).
*
* Owner: WS-4's `GroupService.getGroup`.
*/
const groupChildSchema = groupRecordSchema.pick({
	id: true,
	slug: true,
	name: true
});
/**
* The body of `GET /v1/admin/groups/{id}`: the group row, its principal members, its direct child
* groups, the channel it routes on, and the role slugs every member inherits.
*
* `children` lists the direct children only, never a grandchild; an empty array when nothing is
* nested under the group. `channelAssignments` holds at most one entry —
* `release.channel_assignments` is unique on `(organization_id, group_id)`. No `memberCount`: this
* route does not compute one. `updatedAt` moves on any member, child or field change (WS-4 D15).
*
* Owner: `GroupDetail` in `cloud/dp/core-api/src/admin/group.service.ts`.
*/
const groupDetailSchema = groupRecordSchema.extend({
	members: z.array(groupMemberSchema),
	children: z.array(groupChildSchema),
	channelAssignments: z.array(adminChannelAssignmentSchema),
	roles: z.array(z.string())
});
/**
* `GET /v1/admin/groups` — groups with their member counts. With no query it answers every group of
* the organization and requires `groups:read` (403 otherwise). With `managedByMe=true` it answers the
* groups the caller holds `group:manage` on, for any caller of the organization, and reads no org
* key (R-012). The query is {@link listGroupsQuerySchema}.
*/
const LIST_GROUPS_ROUTE = route("GET", "/v1/admin/groups");
/**
* The query of `GET /v1/admin/groups` (R-003, R-012).
*
* - absent: every group of the organization, for a holder of the `groups:read` org key.
* - `managedByMe=true`: the groups the caller holds `group:manage` on, an app's service bearer
*   included.
*
* The one value is the string literal `'true'`. `managedByMe=false`, `managedByMe=1`, an empty
* value and any other key are refused, 400 on the server (defaults row "Refuse over accept"). A
* literal renders in `openapi.json`; the schema carries no `.transform()`, which would throw in
* `createOpenApiDocument()`.
*
* Owner: `AdminGroupsController.listGroups`, which binds it with `@Query({ schema })`. Consumer:
* WS-5's `GroupsClient.list({ managedByMe: true })`, which sends `managedByMe=true`.
*/
const listGroupsQuerySchema = z.strictObject({ managedByMe: z.literal("true").describe("true: list only the groups the caller holds group:manage on").optional() });
/**
* `GET /v1/admin/groups/{id}` — one group with members, child groups, channel and roles. Requires
* `groups:read`, or `group:manage` on the group (R-012).
*/
const GET_GROUP_ROUTE = route("GET", "/v1/admin/groups/{id}");
/**
* `POST /v1/admin/groups` — create a group. Status 201, 409 on a duplicate slug. Requires
* `groups:manage`, or an app bearer whose organization consented to `platform:groups:manage`; that
* app's principal receives the `group:manage` row on the new group (R-012).
*/
const CREATE_GROUP_ROUTE = route("POST", "/v1/admin/groups");
/**
* `DELETE /v1/admin/groups/{id}` — delete a group. Requires `groups:manage`, or `group:manage` on the
* group.
*/
const DELETE_GROUP_ROUTE = route("DELETE", "/v1/admin/groups/{id}");
/**
* `PATCH /v1/admin/groups/{id}` — rename a group or change its description. Status 200 with the
* saved {@link groupRecordSchema} row; 404 `Group not found`; 400 for a body
* {@link updateGroupRequestSchema} refuses or a path id that is not a uuid. Requires
* `groups:manage`, or `group:manage` on the group. Fill the path with {@link adminGroupPath}.
*/
const UPDATE_GROUP_ROUTE = route("PATCH", "/v1/admin/groups/{id}");
/**
* `PUT /v1/admin/groups/{id}/channel` — set or clear the one release channel the group routes
* on. Requires `channels:manage`, not `groups:manage`.
*/
const SET_GROUP_CHANNEL_ROUTE = route("PUT", "/v1/admin/groups/{id}/channel");
/**
* `POST /v1/admin/groups/{id}/members` — add a member. Status 201, 409 when already a member, 400
* `unknown_subject` when the id is not an active person or an active machine principal of the organization (a deactivated or removed person answers 400 even when already a member). Requires `groups:manage`, or
* `group:manage` on the group. A group that carries an org role, itself or through a parent,
* refuses every caller but a human org owner with 403 (R-012).
* An `external` machine principal that would hold, through the group or a group above it, a role
* it may never hold answers 409 `an external machine principal would hold a role that grants a
* credential-granting permission` (core-api `EXTERNAL_ROLE_CONFLICT_MESSAGE`, the
* `assertPrincipalMayJoinGroup` guard of 2026-10-01-dp-oidc-federation R-025).
*/
const ADD_GROUP_MEMBER_ROUTE = route("POST", "/v1/admin/groups/{id}/members");
/**
* `DELETE /v1/admin/groups/{id}/members/{userId}` — remove a member. Requires `groups:manage`, or
* `group:manage` on the group. The `userId` segment keeps its name, the one `openapi.json` carries,
* and holds the member's `principalId`: a `users.id` or a `machine_principals.id`.
*/
const REMOVE_GROUP_MEMBER_ROUTE = route("DELETE", "/v1/admin/groups/{id}/members/{userId}");
/**
* `POST /v1/admin/groups/{id}/child-groups` — nest an existing group of the organization under the
* group `{id}`. The body is {@link addChildGroupRequestSchema}. Status 201
* `{ message: 'Child group added' }`; 404 when the parent or the child is missing; 409 when the
* child is nested under the parent already; 400 for a self-edge, an edge that closes a cycle, an
* edge across organizations, or an edge that takes the nesting past a depth of 8.
* 409 also when an `external` machine principal under the child would hold, through the parent or
* a group above it, a role it may never hold: `an external machine principal would hold a role
* that grants a credential-granting permission` (core-api `EXTERNAL_ROLE_CONFLICT_MESSAGE`, the
* `assertGroupMayNestUnder` guard of 2026-10-01-dp-oidc-federation R-025, ruling R-016).
* Requires `groups:manage`, or `group:manage` on the parent. A parent that carries an org role,
* itself or through a parent, refuses every caller but a human org owner with 403 (R-012). Fill the
* path with {@link adminGroupChildGroupsPath}.
*/
const ADD_CHILD_GROUP_ROUTE = route("POST", "/v1/admin/groups/{id}/child-groups");
/**
* `DELETE /v1/admin/groups/{id}/child-groups/{childId}` — remove the nesting edge from the parent
* `{id}` to the child `{childId}` (#3597). The two groups stay. Status 200 with
* {@link removeChildGroupResponseSchema}; 404 when the parent is missing or `{childId}` is not a
* direct child of `{id}`; 400 for a path id that is not a uuid. Requires `groups:manage`, or
* `group:manage` on the parent. A parent that carries an org role, itself or through a parent,
* refuses every caller but a human org owner with 403, as the add route does. The write takes the
* organization's nesting lock and writes a `group_child_removed` audit row in its transaction.
* Fill the path with {@link adminGroupChildGroupPath}.
*/
const REMOVE_CHILD_GROUP_ROUTE = route("DELETE", "/v1/admin/groups/{id}/child-groups/{childId}");
/**
* `POST /v1/admin/groups/{id}/child-groups/{childId}/move` — move the child `{childId}` from the
* parent `{id}` to the parent the body {@link moveChildGroupRequestSchema} names, in one
* transaction (I6, #3597). Under the organization's nesting lock core-api:
*
* 1. locks the two parent rows in sorted id order;
* 2. deletes the old edge first, so the checks below see the tree after the move;
* 3. checks the new edge as the add route does, and inserts it;
* 4. writes `group_child_removed` and `group_child_added` audit rows.
*
* Status 200 with {@link moveChildGroupResponseSchema}. Refusals, each of which leaves the old
* edge in place:
*
* - 400: a body the schema refuses, a path id that is not a uuid, a `newParentId` equal to `{id}`
*   or to `{childId}`.
* - 403: the caller lacks `groups:manage` and lacks `group:manage` on `{id}` or on `newParentId`,
*   or either parent carries an org role and the caller is not a human org owner.
* - 404: `{id}` or `newParentId` is missing, or `{childId}` is not a direct child of `{id}`.
* - 409: the new edge closes a cycle, takes the nesting past a depth of 8, or exists already
*   (D-03).
* - 409: an `external` machine principal under the child would hold, through the new parent or a
*   group above it, a role it may never hold: `an external machine principal would hold a role
*   that grants a credential-granting permission` (core-api `EXTERNAL_ROLE_CONFLICT_MESSAGE`, the
*   `assertGroupMayNestUnder` guard the add route runs, 2026-10-01-dp-oidc-federation R-025,
*   ruling R-016).
*
* Fill the path with {@link adminGroupChildGroupMovePath}.
*/
const MOVE_CHILD_GROUP_ROUTE = route("POST", "/v1/admin/groups/{id}/child-groups/{childId}/move");
/**
* Fill `/v1/admin/groups/{id}`. The placeholder is `id`, the name `@Param('id')` puts in
* `openapi.json`. Refuses a value with a slash, a space or any other non-param character.
*/
function adminGroupPath(groupId) {
	return fillPath(GET_GROUP_ROUTE.path, { id: groupId });
}
/** Fill `/v1/admin/groups/{id}/channel`. */
function adminGroupChannelPath(groupId) {
	return fillPath(SET_GROUP_CHANNEL_ROUTE.path, { id: groupId });
}
/** Fill `/v1/admin/groups/{id}/members`. */
function adminGroupMembersPath(groupId) {
	return fillPath(ADD_GROUP_MEMBER_ROUTE.path, { id: groupId });
}
/**
* Fill `/v1/admin/groups/{id}/members/{userId}`. `userId` takes the member's `principalId`, a person's
* or a machine principal's.
*/
function adminGroupMemberPath(groupId, userId) {
	return fillPath(REMOVE_GROUP_MEMBER_ROUTE.path, {
		id: groupId,
		userId
	});
}
/**
* Fill `/v1/admin/groups/{id}/child-groups` with the parent group's id. Refuses a value with a
* slash, a space or any other non-param character, as {@link adminGroupPath} does.
*/
function adminGroupChildGroupsPath(parentGroupId) {
	return fillPath(ADD_CHILD_GROUP_ROUTE.path, { id: parentGroupId });
}
/**
* Fill `/v1/admin/groups/{id}/child-groups/{childId}` with the parent and the child group ids.
* Refuses a value with a slash, a space, a dot-segment or any other non-param character, as
* {@link adminGroupPath} does.
*/
function adminGroupChildGroupPath(parentGroupId, childGroupId) {
	return fillPath(REMOVE_CHILD_GROUP_ROUTE.path, {
		id: parentGroupId,
		childId: childGroupId
	});
}
/**
* Fill `/v1/admin/groups/{id}/child-groups/{childId}/move` with the old parent and the child group
* ids. Refuses a bad value as {@link adminGroupChildGroupPath} does.
*/
function adminGroupChildGroupMovePath(parentGroupId, childGroupId) {
	return fillPath(MOVE_CHILD_GROUP_ROUTE.path, {
		id: parentGroupId,
		childId: childGroupId
	});
}
/** The body of `GET /v1/admin/groups`. A bare array, not a paginated envelope. */
const listGroupsResponseSchema = z.array(groupListItemSchema);
/** The body of `GET /v1/admin/groups/{id}`. */
const getGroupResponseSchema = groupDetailSchema;
/**
* The body of `POST /v1/admin/groups`.
*
* The 256 and 1024 bounds are denial-of-service limits, not vocabularies; real names are under 64.
* `description` is optional and the shell client never sends it. `attributes` is optional, obeys
* the {@link groupAttributesSchema} caps, and an absent map stores `{}`.
*
* Owner: `createGroupSchema` in `cloud/dp/core-api/src/admin/dto/admin-groups.dto.ts`.
*/
const createGroupRequestSchema = z.strictObject({
	name: z.string().min(1).max(256).describe("Display name for the group"),
	slug: z.string().min(1).max(256).describe("URL-safe group identifier"),
	description: z.string().max(1024).describe("Optional free-form description").optional(),
	attributes: groupAttributesSchema.describe("App-namespaced string attributes, <app-slug>.<key> to value").optional()
});
/**
* The body of `POST /v1/admin/groups` — the bare row the insert returned, with no `memberCount`.
*/
const createGroupResponseSchema = groupRecordSchema;
/**
* The body of `PATCH /v1/admin/groups/{id}`. Every key is optional and
* `GroupService.updateGroup` writes only the keys present. `attributes` is a
* {@link groupAttributesPatchSchema} patch: a string sets a key and `null` deletes it. core-api
* merges it with {@link applyGroupAttributesPatch} and answers 400 when the merged map breaks a
* {@link groupAttributesSchema} cap. A body that names `name`, `description` or `attributes` writes
* one `group_updated` audit row (#2557), with `details.changed` the keys the body names. core-api
* compares no value, so a key whose value equals the stored one still counts. `{}` writes no audit
* row, changes no field, and still bumps `updatedAt`, because `GroupsRepository.update` sets it on
* every call.
*
* The bounds are the producer's: `name` 1..256 and `description` at most 1024, the same
* denial-of-service limits {@link createGroupRequestSchema} carries. `description` is not
* nullable on the producer, so a client clears it with the empty string. No `slug`: the slug is
* fixed at create.
*
* Owner: `updateGroupSchema` in `cloud/dp/core-api/src/admin/dto/admin-groups.dto.ts`, an alias of
* this schema since #3331.
*/
const updateGroupRequestSchema = z.strictObject({
	name: z.string().min(1).max(256).optional(),
	description: z.string().max(1024).optional(),
	attributes: groupAttributesPatchSchema.describe("Attribute patch: a string sets a key, null deletes it, an absent key stays").optional()
});
/** The body of `PATCH /v1/admin/groups/{id}` — the saved row, with no `memberCount`. */
const updateGroupResponseSchema = groupRecordSchema;
/**
* The body of `DELETE /v1/admin/groups/{id}` — status 200,
* `{ message: 'Group deleted' }`. The shell client types it `Promise<void>`.
*/
const deleteGroupResponseSchema = z.object({ message: z.string() });
/**
* The body of `PUT /v1/admin/groups/{id}/channel`.
*
* Singular and nullable, matching `release.channel_assignments`' unique constraint on
* `(organization_id, group_id)`: a group holds at most one channel and `null` clears it. A list
* here would be a shape the database cannot represent.
*
* Owner: `setGroupChannelSchema` in `cloud/dp/core-api/src/admin/dto/admin-groups.dto.ts`.
*/
const setGroupChannelRequestSchema = z.strictObject({ channelId: z.uuidv4().nullable().describe("Channel UUID this group routes on, or null to clear it") });
/**
* The body of `PUT /v1/admin/groups/{id}/channel`.
*
* `previousChannelId` is reported because assigning steals: the unique constraint means a group
* cannot hold two channels, so moving it onto one removes it from the other. When the group already
* held the requested channel the service returns early and both keys carry the same value, which
* preserves `created_at` — a timestamp channel resolution reads to break a ring tie.
*
* Owner: `GroupService.setGroupChannel`.
*/
const setGroupChannelResponseSchema = z.object({
	channelId: z.uuid().nullable(),
	previousChannelId: z.uuid().nullable()
});
/**
* The body of `POST /v1/admin/groups/{id}/members`: the principal to add, as exactly one key.
*
* - `principalId`: a `users.id` or a `machine_principals.id` of the caller's organization
*   (decision 7). Any RFC UUID version, because an operator-derived service id is a v8 UUID.
* - `userId`: the pre-2026-09-30 key, a v4 `users.id`, accepted as an alias of `principalId` for one
*   release so the admin app keeps working while it moves.
*
* A body with both keys, with neither, or with any other key is refused before the request leaves
* the client, and 400 by the server's validation pipe (defaults row "Refuse over accept"). Read the
* principal with {@link addGroupMemberPrincipalId}; never read one key alone. core-api answers 400
* `unknown_subject` for a well-formed id that is not an active person or an active machine principal of the org (a deactivated or removed person answers 400 even when already a member); that check is WS-4's
* service, not this schema.
*
* The rule is a `.refine()`, which `createOpenApiDocument()` renders by dropping, so `openapi.json`
* shows both keys optional. A `.transform()` would throw there, so the schema never renames a key.
*
* Owner: `addGroupMemberSchema` in `cloud/dp/core-api/src/admin/dto/admin-groups.dto.ts`, an alias
* of this schema.
*/
const addGroupMemberRequestSchema = z.strictObject({
	principalId: z.uuid().describe("Principal UUID to add as a member: a user or a machine principal of the organization").optional(),
	userId: z.uuidv4().describe("Deprecated alias of principalId for one release: a user UUID to add as a member").optional()
}).refine((body) => body.principalId === void 0 !== (body.userId === void 0), { message: "send exactly one of principalId and userId" });
/**
* The principal a parsed {@link addGroupMemberRequestSchema} body names: `principalId` when present,
* else the `userId` alias.
*
* Callers: WS-4's controller, after the validation pipe has parsed the body, and WS-5's client,
* after its own parse. Throws `TypeError` when the body holds neither key, which a parsed body
* never does, so an unparsed body cannot reach the service as an empty id. Pure.
*/
function addGroupMemberPrincipalId(body) {
	const principalId = body.principalId ?? body.userId;
	if (principalId === void 0) throw new TypeError("addGroupMemberPrincipalId: the body names no principal");
	return principalId;
}
/**
* The body of `POST /v1/admin/groups/{id}/members` — status 201,
* `{ message: 'Member added' }`. The shell client types it `Promise<void>`.
*/
const addGroupMemberResponseSchema = z.object({ message: z.string() });
/**
* The body of `DELETE /v1/admin/groups/{id}/members/{userId}` — status 200,
* `{ message: 'Member removed' }`. The shell client types it `Promise<void>`.
*/
const removeGroupMemberResponseSchema = z.object({ message: z.string() });
/**
* The body of `POST /v1/admin/groups/{id}/child-groups`: the group to nest under `{id}`.
*
* - `childGroupId`: the `auth.groups.id` of an existing group of the caller's organization, a v4
*   UUID as the column's default mints it.
*
* Any other key is refused before the request leaves the client, and 400 by the server's
* validation pipe. A missing child is 404 on the server; this schema checks the form only.
*
* Owner: `addChildGroupSchema` in `cloud/dp/core-api/src/admin/dto/admin-groups.dto.ts`, which
* held the body locally until R-003 moved it here. Consumer: WS-5's
* `GroupsClient.createChild(parentGroupId, childGroupId)`.
*/
const addChildGroupRequestSchema = z.strictObject({ childGroupId: z.uuidv4().describe("Child group UUID to nest inside this group") });
/**
* The body of `POST /v1/admin/groups/{id}/child-groups` — status 201,
* `{ message: 'Child group added' }`.
*/
const addChildGroupResponseSchema = z.object({ message: z.string() });
/**
* The body of `DELETE /v1/admin/groups/{id}/child-groups/{childId}` — status 200,
* `{ message: 'Child group removed' }`.
*/
const removeChildGroupResponseSchema = z.object({ message: z.string() });
/**
* The body of `POST /v1/admin/groups/{id}/child-groups/{childId}/move`: the new parent.
*
* - `newParentId`: the `auth.groups.id` of an existing group of the caller's organization, a v4
*   UUID as the column default mints it.
*
* Any other key is refused before the request leaves the client, and 400 by the server's
* validation pipe. A missing parent is 404 on the server; this schema checks the form only.
*
* Owner: core-api's `AdminGroupsController` (WS-1), through `@Body({ schema })`. Consumer:
* sdk-node's `GroupsClient.move(parentGroupId, childGroupId, newParentGroupId)` (WS-6).
*/
const moveChildGroupRequestSchema = z.strictObject({ newParentId: z.uuidv4().describe("Group UUID of the new parent") });
/**
* The body of `POST /v1/admin/groups/{id}/child-groups/{childId}/move` — status 200,
* `{ message: 'Child group moved' }`.
*/
const moveChildGroupResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. Frozen. */
const groupsRoutes = Object.freeze([
	LIST_GROUPS_ROUTE,
	GET_GROUP_ROUTE,
	CREATE_GROUP_ROUTE,
	DELETE_GROUP_ROUTE,
	SET_GROUP_CHANNEL_ROUTE,
	ADD_GROUP_MEMBER_ROUTE,
	REMOVE_GROUP_MEMBER_ROUTE,
	UPDATE_GROUP_ROUTE,
	ADD_CHILD_GROUP_ROUTE,
	REMOVE_CHILD_GROUP_ROUTE,
	MOVE_CHILD_GROUP_ROUTE
]);
//#endregion
export { ADD_CHILD_GROUP_ROUTE, ADD_GROUP_MEMBER_ROUTE, CREATE_GROUP_ROUTE, DELETE_GROUP_ROUTE, GET_GROUP_ROUTE, GROUP_ATTRIBUTES_MAX_BYTES, GROUP_ATTRIBUTES_MAX_ENTRIES, GROUP_ATTRIBUTES_TOO_LARGE_MESSAGE, GROUP_ATTRIBUTES_TOO_MANY_MESSAGE, GROUP_ATTRIBUTE_KEY_MAX_LENGTH, GROUP_ATTRIBUTE_KEY_PATTERN, GROUP_ATTRIBUTE_VALUE_MAX_LENGTH, GROUP_MEMBER_KINDS, LIST_GROUPS_ROUTE, MACHINE_GROUP_MEMBER_KINDS, MOVE_CHILD_GROUP_ROUTE, REMOVE_CHILD_GROUP_ROUTE, REMOVE_GROUP_MEMBER_ROUTE, SET_GROUP_CHANNEL_ROUTE, UPDATE_GROUP_ROUTE, addChildGroupRequestSchema, addChildGroupResponseSchema, addGroupMemberPrincipalId, addGroupMemberRequestSchema, addGroupMemberResponseSchema, adminChannelAssignmentSchema, adminGroupChannelPath, adminGroupChildGroupMovePath, adminGroupChildGroupPath, adminGroupChildGroupsPath, adminGroupMemberPath, adminGroupMembersPath, adminGroupPath, applyGroupAttributesPatch, createGroupRequestSchema, createGroupResponseSchema, deleteGroupResponseSchema, getGroupResponseSchema, groupAttributesPatchSchema, groupAttributesSchema, groupChildSchema, groupDetailSchema, groupListItemSchema, groupMemberSchema, groupRecordSchema, groupsRoutes, humanGroupMemberSchema, humanPrincipalGroupMemberSchema, listGroupsQuerySchema, listGroupsResponseSchema, machineGroupMemberSchema, moveChildGroupRequestSchema, moveChildGroupResponseSchema, removeChildGroupResponseSchema, removeGroupMemberResponseSchema, setGroupChannelRequestSchema, setGroupChannelResponseSchema, updateGroupRequestSchema, updateGroupResponseSchema };

//# sourceMappingURL=groups.js.map